import React from 'react';
import {
  Flex,
  useColorMode,
  useIsMobileView,
  Text,
  typography,
  color,
} from 'deepstash-ui';
import styles from './CollectionTabs.module.scss';

export const COLLECTIONS_TABS_HEIGHT = 60;

interface CollectionTabsProps {
  tabs: string[];
  currentTabIndex: number;
  onPressToChangeTab: ({ newIndex }: { newIndex: number }) => void;
}

const CollectionTabs: React.FC<CollectionTabsProps> = ({
  tabs,
  currentTabIndex,
  onPressToChangeTab,
}) => {
  const isMobileView = useIsMobileView();
  const { colorMode } = useColorMode();

  const containerRef = React.useRef<HTMLDivElement>(null);
  const tabsRef = React.useRef<HTMLDivElement>(null);
  const [topDiff, setTopDiff] = React.useState<number | undefined>(undefined);

  React.useLayoutEffect(() => {
    const boundingClientRect = containerRef.current?.getBoundingClientRect();
    if (boundingClientRect) {
      setTopDiff(boundingClientRect.top);
    }
  }, []);

  React.useEffect(() => {
    const scrollListener = () => {
      if (topDiff === undefined) {
        return;
      }
      const scrollY = window.scrollY;
      if (scrollY >= topDiff) {
        // Make it sticky
        tabsRef.current?.classList.add(styles.sticky);
      } else {
        // Remove sticky
        tabsRef.current?.classList.remove(styles.sticky);
      }
    };

    addEventListener('scroll', scrollListener);
    return () => {
      removeEventListener('scroll', scrollListener);
    };
  }, [topDiff]);

  return (
    <Flex
      w="100%"
      flexDirection="column"
      alignItems="center"
      ref={containerRef}
      height={COLLECTIONS_TABS_HEIGHT}
    >
      <Flex
        flexDirection="row"
        bgColor={color[colorMode].background}
        width={'100%'}
        justifyContent="center"
        height={COLLECTIONS_TABS_HEIGHT}
        ref={tabsRef}
        alignItems="flex-end"
      >
        {tabs.map((tab, index) => {
          const isTabSelected = currentTabIndex === index;
          return (
            <Flex
              key={`tab-${index}`}
              width={160}
              flexDirection="column"
              alignItems="center"
              justifyContent="flex-end"
              height={'100%'}
              cursor={'pointer'}
              onClick={() => {
                onPressToChangeTab({ newIndex: index });
              }}
            >
              <Text
                {...(isMobileView
                  ? typography.primaryHeadingFonts.small.regular
                  : typography.primaryHeadingFonts.medium.regular)}
                color={
                  isTabSelected
                    ? color[colorMode].text
                    : color[colorMode].textDisabled
                }
              >
                {tab}
              </Text>
              <Flex
                h={2}
                w={'100%'}
                mt={8}
                bgColor={
                  isTabSelected
                    ? color[colorMode].text
                    : color[colorMode].underground
                }
              />
            </Flex>
          );
        })}
      </Flex>
      {/** Invisible rectangle just to avoid 'the jump' when tabs become sticky */}
      <Flex
        width={'100%'}
        height={COLLECTIONS_TABS_HEIGHT}
        position="absolute"
        top={0}
        left={0}
        pointerEvents="none"
      />
    </Flex>
  );
};

export default CollectionTabs;
